import { action, computed } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import { A, pipe } from "@mobily/ts-belt"
import type { Permission } from "@shared/models/permissions"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type { User, UserSummary } from "@shared/models/user"
import planbookUIAction from "cc-frontend/lib/actions/app-state/PLANBOOK_UI_ACTION"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type ModalManager from "cc-frontend/services/modal-manager"
import type PresenceService from "cc-frontend/services/presence"
import type { PresenceData } from "cc-frontend/services/presence"
import type Session from "cc-frontend/services/session"
import { isCachedMembershipActiveSchool } from "cc-frontend/utils/group"
import styles from "./planbook-main-v2.module.css"

interface Args {
  owner: User
  planbook: Planbook
  rotationCalendar: RotationCalendar
  permissions: Array<Permission>
  isInPlannerRoute: boolean
  isInLinkShareableRoute: boolean
  perspective: string
  startDate: string
  endDate: string
  dateRangeString: string
  isFilteringClasses: boolean
  classesLinedUp: boolean
  hiddenCourseIds: Array<string>
}

export default class PlanbookMainComponent extends Component<Args> {
  readonly styles = styles

  @tracked canShowMenu = false
  @tracked isSettingsMenuOpen = false
  @tracked isShareMenuOpen = false

  @tracked isResponsiveLeftNavMenuOpen = false
  @tracked isResponsiveRightNavMenuOpen = false

  @service declare session: Session
  @service declare dispatcher: Dispatcher
  @service declare router
  @service declare modalManager: ModalManager
  @service declare presence: PresenceService

  menuConstraints = [
    {
      to: "window",
      attachment: "together",
      pin: "right",
    },
  ]

  @computed("router.currentRoute.localName")
  get conditionalText() {
    let routeName = this.router?.currentRoute.localName
    if (routeName === "day") {
      return "this Day"
    } else if (routeName === "week") {
      return "this Week"
    } else if (routeName === "month") {
      return "this Month"
    } else if (routeName === "timeline") {
      return "Timeline"
    } else if (routeName === "outline") {
      return "Outline"
    } else {
      return "Standards Tracker"
    }
  }

  get ownerSchoolMembership() {
    return pipe(
      this.args.owner?.attributes.cachedMemberships ?? [],
      A.find(isCachedMembershipActiveSchool)
    )
  }

  get presentUserSummaries(): UserSummary[] {
    // Deduplicate users.
    const presenceByUser = new Map<string, PresenceData>()
    for (const data of this.presence.presenceDataExclUser) {
      presenceByUser.set(data.userId, data)
    }
    const presenceData = [...presenceByUser.values()]
    return presenceData.map(
      (data) =>
        ({
          id: data.userId,
          type: "user-summary",
          attributes: {
            firstName: data.firstName,
            lastName: data.lastName,
            avatarColor: data.avatarColor,
            // Other fields are unused. Just cast.
          },
          relationships: {},
          meta: {},
        } as UserSummary)
    )
  }

  get shownUserSummaries(): UserSummary[] {
    const userSummaries = this.presentUserSummaries
    if (userSummaries.length > 3) {
      // Show the first two and MoreAvatars for the rest.
      return userSummaries.slice(0, 2)
    } else return userSummaries
  }

  get moreUserSummaries(): UserSummary[] {
    const userSummaries = this.presentUserSummaries
    if (userSummaries.length > 3) {
      // Show the first two and MoreAvatars for the rest.
      return userSummaries.slice(2)
    } else return []
  }

  @action
  toggleSettingsMenu() {
    this.isSettingsMenuOpen = !this.isSettingsMenuOpen
  }

  @action
  toggleShareMenu(route) {
    this.isShareMenuOpen = !this.isShareMenuOpen
    if (this.isShareMenuOpen) {
      this.dispatcher.dispatchPreparedAction(
        planbookUIAction({
          planbookId: this.args.planbook.id,
          noun: "planbook_share_menu",
          verb: "open",
        })
      )
    }
    if (typeof route === "string") {
      this.router.transitionTo(route)
    }
  }

  @action
  toggleResponsiveLeftNavMenu(route) {
    this.isResponsiveLeftNavMenuOpen = !this.isResponsiveLeftNavMenuOpen
    if (typeof route === "string") {
      this.router.transitionTo(route)
    }
  }

  @action
  signInOrUp(e) {
    e.preventDefault()

    this.router.transitionTo("user.sign", {
      queryParams: { afterSignInRedirectTo: this.router.currentURL },
    })
  }
}
